import { useState, useEffect } from "react";
import { Users, Copy, Share2, CheckCircle, Globe, Lock, Network, RefreshCw, Mail, UserPlus, X, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const VISIBILITY_OPTIONS = [
  { value: "private",     label: "Private",     desc: "Only you",      icon: Lock,    color: "text-white/40" },
  { value: "connections", label: "Connections", desc: "Your network",  icon: Network, color: "text-[#0A84FF]" },
  { value: "public",      label: "Public",      desc: "Anyone",        icon: Globe,   color: "text-[#00FF88]" },
];

const INVITE_ROLES = [
  { value: "lender",     label: "Lender"     },
  { value: "realtor",    label: "Realtor"    },
  { value: "contractor", label: "Contractor" },
  { value: "title",      label: "Title Co."  },
];

const INVITE_STATUS = {
  sent:     { label: "Sent",     color: "#FF9F0A", icon: Clock },
  accepted: { label: "Accepted", color: "#00FF88", icon: CheckCircle },
  declined: { label: "Declined", color: "#FF3B30", icon: AlertCircle },
};

function InviteExternalModal({ dealId, onClose, onInviteSent }) {
  const [email, setEmail]     = useState("");
  const [name, setName]       = useState("");
  const [role, setRole]       = useState("lender");
  const [msg, setMsg]         = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email) return toast.error("Email is required");
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/deals/${dealId}/invite-participant`,
        { email, name, role, message: msg },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      if (onInviteSent) onInviteSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-bold">Invite External Professional</h3>
            <p className="text-white/40 text-sm mt-0.5">Send a deal collaboration invite via email</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="professional@email.com"
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="invite-email-input" />
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Their Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="John Smith"
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Their Role</label>
            <div className="flex gap-1.5 flex-wrap">
              {INVITE_ROLES.map(r => (
                <button key={r.value} onClick={() => setRole(r.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    role === r.value ? "bg-[#D4AF37] text-black" : "bg-white/[0.04] border border-white/[0.06] text-white/50 hover:text-white"
                  }`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Personal Note (optional)</label>
            <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={2} placeholder="Hi! I'd love your help on this deal…"
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-[#D4AF37]/40" />
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-white/[0.08] text-white/50 hover:text-white text-sm rounded-xl transition-all">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="send-invite-btn"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-xl transition-all disabled:opacity-50">
            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DealNetworkPanel({ deal, onShareSuccess }) {
  const [participants, setParticipants] = useState([]);
  const [invites,      setInvites]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [sharing,      setSharing]      = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [visibility,   setVisibility]   = useState(deal?.network_visibility || "private");
  const [inviteLink,   setInviteLink]   = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    if (!deal?.deal_id) return;
    fetchAll();
  }, [deal?.deal_id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, iRes] = await Promise.all([
        axios.get(`${API}/api/deals/${deal.deal_id}/participants`, { withCredentials: true }),
        axios.get(`${API}/api/deals/${deal.deal_id}/invites`,      { withCredentials: true }),
      ]);
      setParticipants(pRes.data.participants || []);
      setInvites(iRes.data.invites || []);
    } catch (err) {
      console.error("Failed to fetch deal network data", err);
    } finally {
      setLoading(false);
    }
    try {
      const res = await axios.get(`${API}/api/deals/${deal.deal_id}/invite-link`, { withCredentials: true });
      setInviteLink(res.data.invite_link || "");
    } catch {}
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Deal link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToFeed = async () => {
    setSharing(true);
    try {
      await axios.post(`${API}/api/deals/${deal.deal_id}/share-to-feed`, {}, { withCredentials: true });
      toast.success("Deal shared to your network feed!");
      if (onShareSuccess) onShareSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const updateVisibility = async (val) => {
    setVisibility(val);
    try {
      await axios.put(`${API}/api/deals/${deal.deal_id}/network-visibility`, { visibility: val }, { withCredentials: true });
      toast.success(`Visibility set to ${val}`);
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  return (
    <>
      {showInviteModal && <InviteExternalModal dealId={deal.deal_id} onClose={() => setShowInviteModal(false)} onInviteSent={fetchAll} />}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5 space-y-5">
      <div className="flex items-center gap-2">
        <Network className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="font-heading text-sm font-bold text-white uppercase">Deal Network</h3>
      </div>

      {/* Visibility Control */}
      <div className="space-y-2">
        <p className="text-[10px] text-white/30 uppercase tracking-wider">Network Visibility</p>
        <div className="grid grid-cols-3 gap-1.5">
          {VISIBILITY_OPTIONS.map(opt => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => updateVisibility(opt.value)}
                data-testid={`visibility-${opt.value}`}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center transition-all ${
                  visibility === opt.value
                    ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]"
                    : "border-white/[0.06] bg-white/[0.02] hover:border-white/10"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${visibility === opt.value ? "text-[#D4AF37]" : opt.color}`} />
                <span className={`text-[10px] font-semibold ${visibility === opt.value ? "text-[#D4AF37]" : "text-white/50"}`}>
                  {opt.label}
                </span>
                <span className="text-[9px] text-white/20">{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Invite Link */}
      <div className="space-y-2">
        <p className="text-[10px] text-white/30 uppercase tracking-wider">Deal Link</p>
        <div className="flex gap-2">
          <div className="flex-1 bg-black/40 border border-white/[0.06] rounded-lg px-3 py-2 text-[11px] text-white/40 truncate font-mono">
            {inviteLink || "..."}
          </div>
          <button
            onClick={copyLink}
            data-testid="copy-deal-link"
            className="flex items-center gap-1.5 px-3 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-lg text-white/60 hover:text-white text-xs transition-all"
          >
            {copied ? <CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Share to Feed */}
      <button
        onClick={shareToFeed}
        disabled={sharing}
        data-testid="share-to-feed-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/20 text-[#D4AF37] text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
      >
        {sharing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
        Share to Network Feed
      </button>

      {/* Invite External Professional */}
      <button
        onClick={() => setShowInviteModal(true)}
        data-testid="invite-external-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/60 hover:text-white text-sm rounded-lg transition-all"
      >
        <UserPlus className="w-4 h-4" /> Invite External Professional
      </button>

      {/* Sent Invites */}
      {invites.length > 0 && (
        <div className="space-y-2 pt-1 border-t border-white/[0.06]">
          <p className="text-[10px] text-white/30 uppercase tracking-wider flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Sent Invites ({invites.length})
          </p>
          <div className="space-y-1.5">
            {invites.map((inv) => {
              const cfg = INVITE_STATUS[inv.status] || INVITE_STATUS.sent;
              const Icon = cfg.icon;
              return (
                <div key={inv.invite_id} className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.02]">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cfg.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[11px] font-medium truncate">{inv.invitee_email}</p>
                    <p className="text-[9px] text-white/30 capitalize">{inv.invitee_role}</p>
                  </div>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: cfg.color, background: `${cfg.color}15` }}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Participants */}
      <div className="space-y-2 pt-1 border-t border-white/[0.06]">
        <p className="text-[10px] text-white/30 uppercase tracking-wider flex items-center gap-1.5">
          <Users className="w-3 h-3" /> Deal Team ({loading ? "..." : participants.length})
        </p>
        {loading ? (
          <div className="flex justify-center py-3">
            <RefreshCw className="w-4 h-4 text-white/20 animate-spin" />
          </div>
        ) : participants.length === 0 ? (
          <p className="text-[11px] text-white/20 py-2 text-center">No team members yet — assign a lender, realtor, or title company</p>
        ) : (
          <div className="space-y-2">
            {participants.map((p, i) => (
              <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.02]">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: `${p.color}15`, color: p.color }}
                >
                  {p.name?.charAt(0) || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{p.name}</p>
                  <p className="text-[10px]" style={{ color: p.color }}>{p.role}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
